/*
 * Perfil da IA em localStorage — helpers compartilhados (trial v2, porta A).
 *
 * NOTA: ProfileModal.tsx mantém cópias privadas de carregar/salvar com a
 * MESMA chave ('drpera-ia-profile') — não tocamos naquele arquivo (fase
 * completa). Este util existe pro fluxo das duas portas do onboarding
 * in-chat poder anexar o exemplo de estilo sem abrir o modal.
 */
import type { AIProfile } from '../types';
import { pushWebProfileToServer } from '../api/profile';

/** Mesma chave usada pelo ProfileModal */
const STORAGE_KEY = 'drpera-ia-profile';

/** Perfil padrão vazio (espelha PERFIL_PADRAO do ProfileModal) */
const PERFIL_PADRAO: AIProfile = {
  specialty: '',
  idioma: 'pt-BR',
  communicationStyle: '',
  topicsOfInterest: '',
  constraints: '',
};

/** Carrega perfil do localStorage (fallback: perfil vazio) */
export function carregarPerfilLocal(): AIProfile {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return PERFIL_PADRAO;
    return { ...PERFIL_PADRAO, ...(JSON.parse(raw) as Partial<AIProfile>) };
  } catch {
    return PERFIL_PADRAO;
  }
}

/** Persiste perfil no localStorage */
export function salvarPerfilLocal(perfil: AIProfile): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(perfil));
  } catch {
    /* quota/bloqueado — perda graciosa */
  }
}

/** Recorte máximo do exemplo colado que vai pro perfil (~800 bytes total) */
const MAX_EXEMPLO_CHARS = 600;

/**
 * Porta A do onboarding in-chat: anexa o prontuário de exemplo colado pelo
 * médico ao campo communicationStyle do perfil local (recortado a 600 chars)
 * e dispara push best-effort pro backend (no-op se deslogado — o push
 * existente de api/profile.ts preserva learnings server-side).
 *
 * O wrapper fica em pt-BR fixo de propósito: é metadado pro LLM (injetado
 * no system prompt), não texto visível pro médico.
 */
export function appendEstiloExemplo(textoExemplo: string): void {
  const recortado = textoExemplo.trim().slice(0, MAX_EXEMPLO_CHARS);
  if (!recortado) return;

  const perfil = carregarPerfilLocal();
  const bloco =
    '\n\nSegue o estilo deste exemplo real do médico (formato, abreviações, organização):' +
    `\n---\n${recortado}\n---`;

  const atualizado: AIProfile = {
    ...perfil,
    communicationStyle: `${perfil.communicationStyle}${bloco}`.trim(),
  };
  salvarPerfilLocal(atualizado);

  // Best-effort: só faz PUT se há JWT (pushWebProfileToServer já é no-op
  // quando deslogado e NUNCA lança).
  void pushWebProfileToServer(atualizado);
}
